import { useCallback, useMemo, useRef, useState } from "react";
import { usePickerBaseValue } from "./usePickerBaseValue";
import { usePickerChainValue } from "./usePickerChainValue";
import { usePickerParentSelection } from "./usePickerParentSelection";
import { usePickerListRows } from "./usePickerListRows";
import { usePickerListState } from "./usePickerListState";
import {
    usePickerQueryContextValue,
    usePickerRootContextValue,
} from "./usePickerRootContextValue";
import { usePickerSelection } from "./usePickerSelection";
import { collectExclusiveSiblings } from "./pickerExclusiveSections";
import { useShallowStable } from "./usePickerStable";
import { usePickerTypedCommit } from "./usePickerTypedCommit";
import type { UsePickerListRootOptions } from "./usePickerListRoot.types";
import type { PickerItemValue } from "./Picker.types";

export type { UsePickerListRootOptions } from "./usePickerListRoot.types";

/**
 * The state engine shared by every form of `Picker.Select` and by flyout
 * levels: normalised selection, open and query state, tree expansion,
 * the flattened row groups, the footer, and the context value the parts
 * read. Roots only choose the Base UI primitive and layout.
 */
export const usePickerListRoot = <T extends PickerItemValue>({
    kind,
    isMultiple,
    isTextValue = false,
    value,
    defaultValue,
    onValueChange,
    items,
    sections,
    nested = "tree",
    defaultExpandAll = false,
    defaultExpandedValues,
    container,
    flyoutDefaults,
    flyoutQueryScope = "level",
    flyoutAutoOpen = "none",
    chain,
    depth = 0,
    onLeafCommit,
    renderValue,
    selectionSummary,
    parentSelection = "self",
    selectableParents: selectableParentsProp,
    indicateSelectedDescendants = false,
    inheritedIndicatedDescendants,
    filter,
    shouldFilter,
    shouldBypassSelectedLabelQuery = false,
    hasTypedCommit = false,
    topQuery: topQueryProp,
    open,
    defaultOpen = false,
    isAlwaysOpen = false,
    isInline = false,
    isAutoHighlight = false,
    cellEditor,
    onOpenChange,
    inputValue,
    defaultInputValue = "",
    onInputValueChange,
    hasPopupSearch = false,
    emptyBehavior = "show",
    status = "idle",
    hasMore = false,
    onLoadMore,
    loadMoreStatus = "idle",
    renderFooter,
    disabled,
    isInvalid,
    classNames,
    forcedState,
    chevron = "always",
    explicitChevron,
    onBeforeTriggerBlur,
    onBlur,
}: UsePickerListRootOptions<T>) => {
    const allItems = useMemo(
        () => items ?? sections?.flatMap((section) => section.items),
        [items, sections],
    );
    // An inline `flyoutDefaults` or `cellEditor` literal must not invalidate the chain memo
    // on every render; `filter` stays a plain memo dependency (it runs at
    // render time, so the latest predicate must be the one that filtered).
    const stableFlyoutDefaults = useShallowStable(flyoutDefaults);
    const stableCellEditor = useShallowStable(cellEditor);
    const { selectableParents, cascadeDescendants, indicatedDescendants } =
        usePickerParentSelection({
            isMultiple,
            parentSelection,
            selectableParents: selectableParentsProp,
            indicateSelectedDescendants,
            inheritedIndicatedDescendants,
            items: allItems,
        });
    const exclusiveSiblings = useMemo(
        () => collectExclusiveSiblings(sections),
        [sections],
    );
    const selection = usePickerSelection({
        isMultiple,
        value,
        defaultValue,
        onValueChange,
        items: allItems,
        renderValue,
        selectionSummary,
        exclusiveSiblings,
    });

    const state = usePickerListState<T>({
        open,
        defaultOpen,
        onOpenChange,
        inputValue,
        defaultInputValue,
        onInputValueChange,
        hasPopupSearch,
        shouldBypassSelectedLabelQuery,
        selectionLabel: selection.selectionLabel,
    });
    const {
        query,
        handleInputValueChange: setQuery,
        expansion,
        setExpanded,
    } = state;
    // Closing the popup drops any open flyout in the same commit: a level's
    // popup must never be positioned against a row that is already gone.
    const [openFlyoutId, setOpenFlyoutId] = useState<string | null>(null);
    const { handleOpenChange: setOpenState } = state;
    const isMultiSearchBar = isMultiple && !hasTypedCommit;
    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                setOpenFlyoutId(null);
            }
            // Picks keep a multi-select search's text (below), so each open
            // starts from an empty query instead.
            if (nextOpen && isMultiSearchBar && query !== "") {
                setQuery("", "input-clear");
            }
            setOpenState(nextOpen);
        },
        [isMultiSearchBar, query, setOpenState, setQuery],
    );
    const closePopup = useCallback(
        () => handleOpenChange(false),
        [handleOpenChange],
    );
    const openPopup = useCallback(
        () => handleOpenChange(true),
        [handleOpenChange],
    );

    // The text that actually filters rows: a single-select input trigger
    // showing its selected label is not a search, so it neither filters nor
    // puts the tree into its query layout.
    const filterQuery =
        shouldFilter && !state.isSelectedLabelQuery ? query : "";
    const { groups, footer, isPopupHidden } = usePickerListRows<T>({
        items,
        sections,
        expansion,
        isExpandedByDefault: defaultExpandAll,
        defaultExpandedValues,
        query: filterQuery,
        selectedValues: selection.selectedValues,
        selectableParents,
        nested,
        filter,
        renderFooter,
        rawQuery: query,
        isDataDriven: allItems !== undefined,
        status,
        hasMore,
        loadMoreStatus,
        emptyBehavior,
        hasPopupSearch,
    });
    const isOpen = isAlwaysOpen || (state.isOpenRequested && !isPopupHidden);
    // Base UI resets its input when the popup unmounts (`input-clear`, or
    // `none` when it re-syncs to a selection); a popup closed for lack of
    // matches must keep the text the user is still typing. A multi-select
    // pick also clears the input (`input-clear` / `item-press`) while the list
    // is open; a search bar keeps its text so several rows can be picked per
    // query.
    const handleInputValueChange = useCallback(
        (nextQuery: string, reason?: string) => {
            if (
                isPopupHidden &&
                (reason === "input-clear" || reason === "none")
            ) {
                return;
            }
            if (
                isMultiSearchBar &&
                isOpen &&
                (reason === "input-clear" || reason === "item-press")
            ) {
                return;
            }
            setQuery(nextQuery, reason);
        },
        [isMultiSearchBar, isOpen, isPopupHidden, setQuery],
    );

    const { handleBaseOpenChange: handleChainOpenChange, chainValue } =
        usePickerChainValue({
            chain,
            depth,
            kind,
            isMultiple,
            selectedValues: selection.selectedValues,
            setSelectedValues: selection.setSelectedValues,
            registerItem: selection.registerItem,
            onLeafCommit,
            container,
            flyoutDefaults: stableFlyoutDefaults,
            flyoutQueryScope,
            flyoutAutoOpen,
            isAutoHighlight,
            cellEditor: stableCellEditor,
            handleOpenChange,
            closePopup,
        });
    // An outside press (or focus leaving) closes without pulling focus back
    // into the trigger; Escape and item presses still return it.
    const returnFocusOnCloseRef = useRef(true);
    const handleBaseOpenChange = useCallback(
        (
            nextOpen: boolean,
            details: { reason: string; event: Event; cancel: () => void },
        ) => {
            returnFocusOnCloseRef.current =
                nextOpen ||
                (details.reason !== "outside-press" &&
                    details.reason !== "focus-out");
            handleChainOpenChange(nextOpen, details);
        },
        [handleChainOpenChange],
    );

    // Shared with `usePickerBaseValue` below: flipped there the instant a
    // row press commits for real, consumed here on the very next call so a
    // same-tick stale echo of that press (see usePickerTypedCommit's doc)
    // can't silently re-commit the label the trigger was seeded with.
    const suppressNextTypedCommitRef = useRef(false);
    const commitTypedText = usePickerTypedCommit<T>({
        isEnabled: hasTypedCommit,
        isMultiple,
        items: allItems,
        selectableParents,
        query,
        selectedValues: selection.selectedValues,
        setSelectedValues: selection.setSelectedValues,
        setQuery,
        resetQuery: state.resetQuery,
        closePopup,
        suppressNextCommitRef: suppressNextTypedCommitRef,
    });

    const contextValue = usePickerRootContextValue({
        kind,
        isMultiple,
        isTextValue,
        disabled,
        isInvalid,
        selectableParents,
        parentSelection,
        cascadeDescendants,
        indicatedDescendants,
        exclusiveSiblings,
        selection,
        chainRegisterItem: chain?.registerItem,
        classNames,
        hasPopupSearch,
        isInline,
        openPopup,
        closePopup,
        handleInputValueChange,
        isPopupOpen: isOpen,
        returnFocusOnCloseRef,
        forcedState,
        chevron,
        explicitChevron,
        chainId: chainValue.chainId,
        onBeforeTriggerBlur,
        onBlur,
        hasMore,
        loadMoreStatus,
        onLoadMore,
    });

    const queryValue = usePickerQueryContextValue({
        query,
        filterQuery,
        searchText: state.isSelectedLabelQuery ? "" : query,
        topQuery: topQueryProp ?? filterQuery,
        isPopupHidden,
        commitTypedText,
    });

    const { baseValue, handleBaseValueChange } = usePickerBaseValue({
        isMultiple,
        selectedValues: selection.selectedValues,
        commitValues: selection.commitValues,
        suppressNextTypedCommitRef,
    });

    return {
        selection,
        contextValue,
        queryValue,
        chainValue,
        nested,
        depth,
        groups,
        footer,
        query,
        filterQuery,
        handleInputValueChange,
        resetQuery: state.resetQuery,
        isOpen,
        handleOpenChange,
        handleBaseOpenChange,
        openFlyoutId,
        setOpenFlyoutId,
        setExpanded,
        baseValue,
        handleBaseValueChange,
    };
};

/** Everything `usePickerListRoot` hands a root, for the shell and content helpers. */
export type PickerListRootState<T extends PickerItemValue> = ReturnType<
    typeof usePickerListRoot<T>
>;
